import { useState } from 'react';
import { Box, Group, Text } from '@mantine/core';
import { Folder, FolderOpen, ChevronRight, ChevronDown, Library } from 'lucide-react';
import { FolderNode } from './folderUtils';

interface FolderTreeItemProps {
  node: FolderNode;
  selectedId: string | number | null;
  onSelect: (node: FolderNode) => void;
  depth?: number;
}

export default function FolderTreeItem({
  node,
  selectedId,
  onSelect,
  depth = 0,
}: FolderTreeItemProps) {
  const [expanded, setExpanded] = useState(true);
  const isSelected = selectedId === node.id;
  const hasChildren = node.children.length > 0;
  const isSystem = node.originalCollection.IsSystemCollection;

  const handleClick = () => {
    onSelect(node);
    if (hasChildren) setExpanded(prev => !prev);
  };

  const FolderIcon = isSystem
    ? Library
    : isSelected ? FolderOpen : Folder;

  const iconColor = isSystem ? '#6a5acd' : isSelected ? '#8a6a4a' : '#6f675d';

  return (
    <Box>
      <Group
        gap={4}
        wrap="nowrap"
        style={{
          paddingLeft: 8 + depth * 16,
          paddingRight: 8,
          paddingTop: isSystem ? 7 : 5,
          paddingBottom: isSystem ? 7 : 5,
          borderRadius: 6,
          cursor: 'pointer',
          backgroundColor: isSelected ? '#eee9df' : 'transparent',
          border: `1px solid ${isSelected ? '#b8aa98' : 'transparent'}`,
          transition: 'background-color 0.15s ease',
        }}
        onClick={handleClick}
        onMouseEnter={e => {
          if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = '#faf8f4';
        }}
        onMouseLeave={e => {
          if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
        }}
      >
        {hasChildren ? (
          expanded
            ? <ChevronDown size={12} color="#888" style={{ flexShrink: 0 }} />
            : <ChevronRight size={12} color="#888" style={{ flexShrink: 0 }} />
        ) : (
          <Box style={{ width: 12, flexShrink: 0 }} />
        )}

        <FolderIcon size={isSystem ? 16 : 15} color={iconColor} style={{ flexShrink: 0 }} />

        <Text
          size={isSystem ? 'sm' : 'sm'}
          fw={isSystem ? 700 : isSelected ? 600 : 400}
          c={isSystem ? '#4a3f8a' : isSelected ? '#2f271f' : '#4a4335'}
          style={{
            userSelect: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={node.name}
        >
          {node.name}
        </Text>
      </Group>

      {hasChildren && expanded && (
        <Box>
          {node.children.map(child => (
            <FolderTreeItem
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
